import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  useTheme,
  Fade
} from '@mui/material';
import {
  ArrowRight,
  Home,
  Bed,
  ChefHat,
  Bath,
  Baby,
  TreePine
} from 'lucide-react';

// Editorial Feature Card Component - Reusable Pattern
const FeatureCard = ({ icon: Icon, title, description, delay }) => {
  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%', 
          textAlign: 'center',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            borderColor: '#FF5722'
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              color: 'white',
              boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
            }}
          >
            <Icon size={32} />
          </Box>
          
          <Typography 
            variant="h5" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 2,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body1" 
            color="#666" 
            sx={{ 
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

const ResidentialDesignPage = () => {
  const [showElements, setShowElements] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: Home,
      title: 'Living Spaces',
      description: 'Beautiful living rooms designed for comfort, entertainment, and family gatherings.'
    },
    {
      icon: Bed,
      title: 'Bedrooms',
      description: 'Peaceful bedroom retreats that promote rest and reflect personal style.'
    },
    {
      icon: ChefHat,
      title: 'Kitchens',
      description: 'Functional and beautiful kitchens that become the heart of your home.'
    },
    {
      icon: Bath,
      title: 'Bathrooms',
      description: 'Spa-like bathroom sanctuaries with luxurious finishes and smart storage.'
    },
    {
      icon: Baby,
      title: 'Children\'s Rooms',
      description: 'Creative and functional spaces that grow with your children.'
    },
    {
      icon: TreePine,
      title: 'Outdoor Living',
      description: 'Beautiful outdoor spaces that extend your living area into nature.'
    }
  ];

  const projectTypes = [
    {
      title: 'Whole Home Renovation',
      description: 'Complete transformation of your entire home with cohesive design.',
      image: '/images/whole-home.jpg',
      features: ['Full design consultation', 'Space planning', 'Material selection', 'Project management']
    },
    {
      title: 'Room Refresh',
      description: 'Update individual rooms with new furniture, colors, and accessories.',
      image: '/images/room-refresh.jpg',
      features: ['Color consultation', 'Furniture selection', 'Styling services', 'Budget-friendly options']
    },
    {
      title: 'New Construction',
      description: 'Design your dream home from the ground up with custom solutions.',
      image: '/images/new-construction.jpg',
      features: ['Architectural input', 'Custom millwork', 'Lighting design', 'Technology integration']
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section - Editorial Pattern */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  {/* Badge Component */}
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 3, 
                      py: 1, 
                      background: 'rgba(255,87,34,0.08)',
                      border: '1px solid rgba(255,87,34,0.2)',
                      borderRadius: 0,
                      mb: 4
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#FF5722"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Residential Design
                    </Typography>
                  </Box>
                  
                  {/* Headline */}
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="#1A1A1A"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Your Dream{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Home
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      Awaits
                    </Box>
                  </Typography>
                  
                  {/* Subtitle */}
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666"
                    sx={{ 
                      mb: 8,
                      lineHeight: 1.7,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    Create the perfect family home with personalized designs that 
                    reflect your lifestyle and support your daily needs.
                  </Typography>

                  {/* CTA Buttons */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowRight />}
                      sx={{
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                        }
                      }}
                    >
                      Start Your Project
                    </Button>
                    
                    <Button
                      component={RouterLink}
                      to="/portfolio"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: '#1A1A1A',
                        color: '#1A1A1A',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 1,
                        borderWidth: 2,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#FF5722',
                          color: '#FF5722',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(255,87,34,0.2)'
                        }
                      }}
                    >
                      View Portfolio
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Fade in={showElements} timeout={1200}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 500,
                      background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/residential-hero.jpg"
                      alt="Residential Design"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.9)'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 30,
                        left: 30,
                        right: 30,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                        p: 3,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        Family Home Design
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="#666"
                        sx={{ 
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        Suburban Home - Complete Renovation
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Room Types Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Room Specialties
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Every Room Perfected
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              color="#666" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              From cozy bedrooms to functional kitchens, we design every space with purpose and style.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <FeatureCard {...feature} delay={200 * (index + 1)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Project Types Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Project Options
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Design Solutions for Every Need
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              color="#666" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Whether you're building new, renovating, or refreshing, we have the perfect solution.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {projectTypes.map((project, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={showElements} style={{ transitionDelay: `${400 * (index + 1)}ms` }}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      background: '#FFFFFF',
                      border: '1px solid #E0E0E0',
                      borderRadius: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        borderColor: '#FF5722'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 250,
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        component="img"
                        src={project.image}
                        alt={project.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          filter: 'brightness(0.9)'
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 3, height: 'calc(100% - 250px)', display: 'flex', flexDirection: 'column' }}>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {project.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="#666"
                        paragraph
                        sx={{ 
                          lineHeight: 1.6,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                          flexGrow: 1
                        }}
                      >
                        {project.description}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {project.features.map((feature, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                background: '#FF5722',
                                borderRadius: '50%',
                                mr: 2
                              }}
                            />
                            <Typography 
                              variant="body2" 
                              color="#666"
                              sx={{ 
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }}
                            >
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="overline" 
                color="#999" 
                sx={{ 
                  mb: 2, 
                  display: 'block',
                  fontWeight: 500,
                  letterSpacing: 3,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Our Promise
              </Typography>
              <Typography 
                variant="h2" 
                component="h2" 
                color="#1A1A1A" 
                sx={{ 
                  mb: 4,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Family-Focused Design
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                We understand that your home is where life happens. Our residential designs prioritize 
                functionality, comfort, and beauty, creating spaces that work for your family today 
                and adapt as your needs evolve.
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                From busy morning routines to quiet evening moments, every design decision is made 
                with your family's lifestyle in mind, ensuring your home truly works for you.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {[
                  { number: '300+', label: 'Homes Designed' },
                  { number: '15+', label: 'Years Experience' },
                  { number: '99%', label: 'Client Satisfaction' },
                  { number: '24/7', label: 'Project Support' }
                ].map((stat, index) => (
                  <Grid item xs={6} key={index}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 3,
                        background: '#FFFFFF',
                        border: '1px solid #E0E0E0',
                        borderRadius: 0,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                          borderColor: '#FF5722'
                        }
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        component="div" 
                        sx={{ 
                          color: '#FF5722',
                          fontWeight: 700,
                          mb: 1,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="#666"
                        sx={{ 
                          fontWeight: 500,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h2" 
                component="h2" 
                color="white" 
                sx={{ 
                  mb: 3,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Design Your Dream Home
              </Typography>
              <Typography 
                variant="h5" 
                component="p" 
                color="white" 
                sx={{ 
                  mb: 6, 
                  opacity: 0.9,
                  fontWeight: 400,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Let's create a home that perfectly reflects your family's unique lifestyle and needs.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowRight />}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.3)'
                    }
                  }}
                >
                  Get Started Today
                </Button>
                
                <Button
                  component={RouterLink}
                  to="/portfolio"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  View Our Homes
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default ResidentialDesignPage;